/**
 * Markdown — minimal renderer for legal docs + blog posts.
 * Supports: frontmatter (stripped), #/##/### headings, ordered + unordered
 * lists with one level of nesting, paragraphs, inline **bold**, [text](url),
 * `code`, ![alt](url) images (public/ paths), pipe tables, `---` rules, and
 * [^n] footnote refs → <sup> + definition lines → small source notes.
 *
 * ponytail: no markdown dep; a ~60-line renderer beats adding marked/react-markdown
 * for static pages. Upgrade to a lib if docs grow rich formatting.
 */

import { Fragment, type ReactNode } from 'react'

// --- inline: ![alt](url), **bold**, [text](url), `code`, [^n] footnote ref
// (image first: its syntax is a superset-prefix of the link syntax, so it
// must match first) ---
function renderInline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = []
  const re = /(!\[([^\]]*)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`|\[\^(\d+)\])/g
  let rest = text
  let i = 0
  while (rest.length) {
    re.lastIndex = 0
    const m = re.exec(rest)
    if (!m || m.index === undefined) {
      nodes.push(rest)
      break
    }
    if (m.index > 0) nodes.push(rest.slice(0, m.index))
    if (m[8] !== undefined) {
      nodes.push(<sup key={`${keyBase}-fn${i}`} className="text-[0.75em] text-on-surface-variant">{m[8]}</sup>)
    } else if (m[3] !== undefined) {
      // video syntax: ![alt](video.mp4) or ![alt](video.webm) — same markdown
      // image syntax, rendered as an autoplaying muted loop (demo clips).
      // controls is REQUIRED (issue #6): a control-less autoplaying clip looks
      // like a player but swallows taps — u3 rage-clicked the welcome demo
      // video exactly there; on slow networks the black frame reads "broken".
      const isVideo = /\.(mp4|webm)$/i.test(m[3])
      nodes.push(
        isVideo ? (
          <video
            key={`${keyBase}-vid${i}`}
            autoPlay
            loop
            muted
            playsInline
            controls
            className="rounded-lg border border-outline-variant my-2 w-full"
          >
            <source src={m[3]} type={m[3].endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
          </video>
        ) : (
          <img
            key={`${keyBase}-img${i}`}
            src={m[3]}
            alt={m[2] ?? ''}
            loading="lazy"
            className="rounded-lg border border-outline-variant my-2 w-full"
          />
        ),
      )
    } else if (m[4] !== undefined) {
      nodes.push(<strong key={`${keyBase}-b${i}`} className="font-semibold text-on-surface">{m[4]}</strong>)
    } else if (m[5] !== undefined) {
      const url = m[6]
      const external = /^https?:/.test(url)
      // Internal piccard.app links don't need target="_blank
      const isInternal = external && /piccard\.app/.test(url)
      nodes.push(
        (external && !isInternal) ? (
          <a key={`${keyBase}-l${i}`} href={url} target="_blank" rel="noopener noreferrer" className="underline hover:text-on-surface">
            {m[5]}
          </a>
        ) : (
          <a key={`${keyBase}-l${i}`} href={url} className="underline hover:text-on-surface">{m[5]}</a>
        ),
      )
    } else if (m[7] !== undefined) {
      nodes.push(
        <code key={`${keyBase}-c${i}`} className="rounded bg-surface-container px-1.5 py-0.5 text-[0.85em] font-mono">
          {m[7]}
        </code>,
      )
    }
    rest = rest.slice(m.index + m[0].length)
    i++
  }
  return nodes
}

type Block =
  | { type: 'h1' | 'h2' | 'h3' | 'p'; text: string }
  | { type: 'hr' }
  | { type: 'fn'; text: string }
  | { type: 'table'; rows: string[][] }
  | { type: 'list'; ordered: boolean; items: { depth: number; text: string }[] }

function parse(md: string): Block[] {
  const body = md.replace(/^---\n[\s\S]*?\n---\n/, '')
  const blocks: Block[] = []
  let para: string[] = []
  let list: { ordered: boolean; items: { depth: number; text: string }[] } | null = null
  let table: string[][] | null = null

  const flushPara = () => {
    if (para.length) blocks.push({ type: 'p', text: para.join(' ') })
    para = []
  }
  const flushList = () => {
    if (list && list.items.length) blocks.push({ type: 'list', ordered: list.ordered, items: list.items })
    list = null
  }
  const flushTable = () => {
    if (table && table.length) blocks.push({ type: 'table', rows: table })
    table = null
  }

  for (const raw of body.split('\n')) {
    const t = raw.trim()
    if (!t) {
      flushPara()
      flushList()
      flushTable()
      continue
    }
    // table row (header + body; the |---|---| alignment row is skipped)
    if (t.startsWith('|')) {
      flushPara()
      flushList()
      const cells = t.slice(1, t.endsWith('|') ? -1 : undefined).split('|').map((c) => c.trim())
      if (/^[-:| ]+$/.test(t)) continue
      table ??= []
      table.push(cells)
      continue
    }
    flushTable()
    // footnote definition: [^1]: text → small source note
    const fn = t.match(/^\[\^(\d+)\]:\s*(.*)$/)
    if (fn) { flushPara(); flushList(); blocks.push({ type: 'fn', text: `${fn[1]}. ${fn[2]}` }); continue }
    // horizontal rule
    if (/^(-{3,}|\*{3,})$/.test(t)) { flushPara(); flushList(); blocks.push({ type: 'hr' }); continue }
    const depth = raw.length - raw.trimStart().length >= 2 ? 1 : 0
    const ul = /^[-*]\s+/.test(t)
    const ol = /^\d+\.\s+/.test(t)
    if (ul || ol) {
      flushPara()
      const text = t.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '')
      if (!list) list = { ordered: ol, items: [] }
      list.items.push({ depth, text })
      continue
    }
    if (/^###\s+/.test(t)) { flushPara(); flushList(); blocks.push({ type: 'h3', text: t.replace(/^###\s+/, '') }); continue }
    if (/^##\s+/.test(t))  { flushPara(); flushList(); blocks.push({ type: 'h2', text: t.replace(/^##\s+/, '') }); continue }
    if (/^#\s+/.test(t))   { flushPara(); flushList(); blocks.push({ type: 'h1', text: t.replace(/^#\s+/, '') }); continue }
    flushList()
    para.push(t)
  }
  flushPara()
  flushList()
  flushTable()
  return blocks
}

function List({ block }: { block: Extract<Block, { type: 'list' }> }) {
  const Tag = block.ordered ? 'ol' : 'ul'
  // group each depth-0 item with its following depth-1 items
  const groups: { text: string; nested: string[] }[] = []
  for (const it of block.items) {
    if (it.depth === 0) groups.push({ text: it.text, nested: [] })
    else if (groups.length) groups[groups.length - 1].nested.push(it.text)
  }
  return (
    <Tag className={`${block.ordered ? 'list-decimal' : 'list-disc'} pl-6 space-y-1.5 text-on-surface-variant`}>
      {groups.map((g, i) => (
        <li key={i}>
          {renderInline(g.text, `li${i}`)}
          {g.nested.length > 0 && (
            <ul className="list-disc pl-6 mt-1.5 space-y-1.5">
              {g.nested.map((n, j) => (
                <li key={j}>{renderInline(n, `li${i}-${j}`)}</li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </Tag>
  )
}

export function Markdown({ source }: { source: string }) {
  const blocks = parse(source)
  return (
    <div className="space-y-4">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'h1':
            return <h1 key={i} className="font-headline font-bold text-3xl text-on-surface mt-2">{b.text}</h1>
          case 'h2':
            return <h2 key={i} className="font-headline font-semibold text-xl text-on-surface pt-6">{b.text}</h2>
          case 'h3':
            return <h3 key={i} className="font-headline font-semibold text-lg text-on-surface pt-4">{b.text}</h3>
          case 'p':
            return <p key={i} className="leading-relaxed text-on-surface-variant">{renderInline(b.text, `p${i}`)}</p>
          case 'hr':
            return <hr key={i} className="border-outline-variant my-6" />
          case 'fn':
            return <p key={i} className="text-sm text-on-surface-variant/70 leading-snug">{renderInline(b.text, `fn${i}`)}</p>
          case 'table':
            return (
              <div key={i} className="overflow-x-auto my-4">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-outline-variant text-left text-on-surface">
                      {b.rows[0]?.map((c, j) => <th key={j} className="py-2 pr-4 font-semibold">{renderInline(c, `th${j}`)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.slice(1).map((row, r) => (
                      <tr key={r} className="border-b border-outline-variant/50 align-top">
                        {row.map((c, j) => <td key={j} className="py-2 pr-4 text-on-surface-variant">{renderInline(c, `td${r}-${j}`)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          case 'list':
            return <List key={i} block={b} />
          default:
            return <Fragment key={i} />
        }
      })}
    </div>
  )
}
